import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { JSONContent } from '@tiptap/react';
import { BellOff, Hash, Lock, Megaphone, MessageSquare, MoreHorizontal, Plus, Rocket, Smile, Users, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { RealtimeEvent, Rooms } from '@kanbix/shared';
import { ItemKeyLink, UserChip } from '@/components/item-bits';
import { isEmptyDoc, RichEditor, RichViewer } from '@/components/rich-editor';
import { Avatar, Badge, Button, Dialog, EmptyState, Field, Input, Menu, MenuContent, MenuItem, MenuTrigger, PageLoader, Select, Switch } from '@/components/ui';
import { errorMessage, http } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useAiStatus, useProjects, useUserMap, useUsers } from '@/lib/queries';
import { CatchUpButton } from '@/features/ai/AiTools';
import { emitTyping, onRealtime, subscribeRoom } from '@/lib/socket';
import { useUi } from '@/lib/ui-store';
import { cn, fmtDate } from '@/lib/utils';

export interface ChannelSummary {
  id: string;
  type: 'team' | 'qa' | 'announcements' | 'release' | 'custom' | 'dm' | 'group';
  name: string;
  topic: string | null;
  isPrivate: boolean;
  archived: boolean;
  projectKey: string | null;
  releaseId: string | null;
  lastMessageAt: string | null;
  memberIds?: string[];
  isMember: boolean;
  unread: number;
}
interface ChannelDetail extends ChannelSummary {
  members: string[];
  canPost: boolean;
  canManage: boolean;
  muted: boolean;
}
interface Msg {
  id: string;
  channelId: string;
  authorId: string | null;
  kind: string;
  body: JSONContent | null;
  text: string;
  meta: Record<string, unknown> | null;
  threadParentId: string | null;
  replyCount: number;
  editedAt: string | null;
  deleted: boolean;
  createdAt: string;
  reactions: { emoji: string; count: number; mine: boolean }[];
  linkedItem: { key: string; title: string } | null;
}

const EMOJI = ['👍', '🎉', '✅', '👀', '🙏', '🚀'];
export const useChannels = () => useQuery({ queryKey: ['channels'], queryFn: () => http.get<ChannelSummary[]>('/channels'), staleTime: 15_000 });

const icon = (c: ChannelSummary) =>
  c.type === 'announcements' ? <Megaphone className="h-3.5 w-3.5" /> : c.type === 'release' ? <Rocket className="h-3.5 w-3.5" /> : c.type === 'dm' || c.type === 'group' ? <Users className="h-3.5 w-3.5" /> : c.isPrivate ? <Lock className="h-3.5 w-3.5" /> : <Hash className="h-3.5 w-3.5" />;

/** Channels and DMs (FR-MSG-3..8, 11). */
export default function ChatPage() {
  const { channelId } = useParams();
  const navigate = useNavigate();
  const { data: channels, isLoading } = useChannels();
  const { setPageContext } = useUi();
  const [creating, setCreating] = useState<'channel' | 'dm' | null>(null);
  useEffect(() => setPageContext({ route: channelId ? `/chat/${channelId}` : '/chat' }), [channelId, setPageContext]);
  useEffect(() => {
    if (!channelId && channels?.length) navigate(`/chat/${(channels.find((c) => c.type === 'team') ?? channels[0]).id}`, { replace: true });
  }, [channelId, channels, navigate]);
  const groups = useMemo(() => {
    const byProject = new Map<string, ChannelSummary[]>();
    const dms: ChannelSummary[] = [];
    for (const c of channels ?? []) {
      if (c.type === 'dm' || c.type === 'group') dms.push(c);
      else byProject.set(c.projectKey ?? '—', [...(byProject.get(c.projectKey ?? '—') ?? []), c]);
    }
    return { byProject, dms: dms.sort((a, b) => (b.lastMessageAt ?? '').localeCompare(a.lastMessageAt ?? '')) };
  }, [channels]);
  if (isLoading) return <PageLoader />;
  return (
    <div className="flex h-full min-h-0">
      <aside className="hidden w-64 shrink-0 flex-col overflow-y-auto border-r border-border bg-surface md:flex">
        {[...groups.byProject.entries()].map(([key, list]) => (
          <div key={key} className="px-2 pt-3">
            <div className="flex items-center px-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-subtle">
              {key}
              <button className="ml-auto rounded p-0.5 hover:bg-muted" onClick={() => setCreating('channel')} aria-label="New channel">
                <Plus className="h-3.5 w-3.5" />
              </button>
            </div>
            {list.map((c) => (
              <ChannelLink key={c.id} c={c} active={c.id === channelId} />
            ))}
          </div>
        ))}
        <div className="px-2 pt-3">
          <div className="flex items-center px-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-subtle">
            Direct messages
            <button className="ml-auto rounded p-0.5 hover:bg-muted" onClick={() => setCreating('dm')} aria-label="New message">
              <Plus className="h-3.5 w-3.5" />
            </button>
          </div>
          {groups.dms.map((c) => (
            <ChannelLink key={c.id} c={c} active={c.id === channelId} />
          ))}
          {!groups.dms.length && <div className="px-2 py-1 text-xs text-subtle">No conversations yet.</div>}
        </div>
      </aside>
      <div className="min-w-0 flex-1">{channelId ? <ChannelView key={channelId} channelId={channelId} /> : <EmptyState title="Pick a channel" />}</div>
      {creating === 'channel' && <NewChannelDialog onClose={() => setCreating(null)} />}
      {creating === 'dm' && <NewDmDialog onClose={() => setCreating(null)} />}
    </div>
  );
}

function ChannelLink({ c, active }: { c: ChannelSummary; active: boolean }) {
  return (
    <Link to={`/chat/${c.id}`} className={cn('flex items-center gap-1.5 rounded px-2 py-1 text-sm', active ? 'bg-brand-soft text-brand' : 'hover:bg-muted', c.unread > 0 && !active && 'font-semibold')}>
      {icon(c)}
      <span className="min-w-0 flex-1 truncate">{c.name}</span>
      {c.unread > 0 && !active && <Badge className="bg-brand text-brand-fg">{c.unread}</Badge>}
    </Link>
  );
}

function ChannelView({ channelId }: { channelId: string }) {
  const qc = useQueryClient();
  const me = useAuth((s) => s.user);
  const room = Rooms.channel(channelId);
  const { data: ch, error } = useQuery({ queryKey: ['channel', channelId], queryFn: () => http.get<ChannelDetail>(`/channels/${channelId}`) });
  const { data: messages } = useQuery({ queryKey: ['messages', channelId], queryFn: () => http.get<Msg[]>(`/channels/${channelId}/messages`), enabled: !!ch });
  const [thread, setThread] = useState<string | null>(null);
  const [presence, setPresence] = useState<{ id: string; name: string }[]>([]);
  const [typing, setTyping] = useState<Record<string, { name: string; at: number }>>({});
  const bottom = useRef<HTMLDivElement>(null);
  useEffect(() => subscribeRoom(room), [room]);
  useEffect(() => {
    const offP = onRealtime<{ room: string; users: { id: string; name: string }[] }>(RealtimeEvent.PRESENCE, (e) => e.room === room && setPresence(e.users));
    const offT = onRealtime<{ room: string; userId: string; name: string }>(RealtimeEvent.TYPING, (e) => e.room === room && setTyping((t) => ({ ...t, [e.userId]: { name: e.name, at: Date.now() } })));
    const tick = setInterval(() => setTyping((t) => Object.fromEntries(Object.entries(t).filter(([, v]) => Date.now() - v.at < 4000))), 1500);
    return () => {
      offP();
      offT();
      clearInterval(tick);
    };
  }, [room]);
  useEffect(() => {
    if (!ch || !messages) return;
    bottom.current?.scrollIntoView({ block: 'end' });
    if (ch.isMember) void http.post(`/channels/${channelId}/read`).then(() => qc.invalidateQueries({ queryKey: ['channels'] }));
  }, [messages, ch, channelId, qc]);
  const join = useMutation({ mutationFn: () => http.post(`/channels/${channelId}/join`), onSuccess: () => qc.invalidateQueries({ queryKey: ['channel', channelId] }) });
  const mute = useMutation({ mutationFn: (muted: boolean) => http.put(`/channels/${channelId}/mute`, { muted }), onSuccess: () => qc.invalidateQueries({ queryKey: ['channel', channelId] }) });
  if (error) return <EmptyState title="Channel not available">{errorMessage(error)}</EmptyState>;
  if (!ch) return <PageLoader />;
  const typers = Object.entries(typing).filter(([id]) => id !== me?.id).map(([, v]) => v.name);
  return (
    <div className="flex h-full min-h-0">
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex items-center gap-2 border-b border-border px-4 py-2">
          {icon(ch)}
          <h1 className="font-semibold">{ch.name}</h1>
          {ch.topic && <span className="truncate text-xs text-subtle">{ch.topic}</span>}
          {ch.releaseId && (
            <Link to={`/releases/${ch.releaseId}`} className="text-xs text-brand hover:underline">
              Release
            </Link>
          )}
          <div className="ml-auto flex items-center gap-2">
            <ChannelCatchUp channelId={ch.id} />
            <div className="flex -space-x-1.5" title={presence.map((p) => p.name).join(', ') + ' viewing'}>
              {presence.slice(0, 5).map((p) => (
                <Avatar key={p.id} name={p.name} size={22} className="ring-2 ring-surface" />
              ))}
            </div>
            <span className="text-xs text-subtle">{ch.members.length} members</span>
            {!ch.isMember && ch.type !== 'dm' && ch.type !== 'group' && (
              <Button size="sm" variant="outline" onClick={() => join.mutate()}>
                Join
              </Button>
            )}
            {ch.isMember && (
              <Button size="icon" variant="ghost" onClick={() => mute.mutate(!ch.muted)} aria-label={ch.muted ? 'Unmute' : 'Mute'} title={ch.muted ? 'Muted' : 'Mute notifications'}>
                <BellOff className={cn('h-4 w-4', ch.muted ? 'text-brand' : 'text-subtle')} />
              </Button>
            )}
          </div>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
          {!messages?.length && <div className="py-10 text-center text-sm text-subtle">No messages yet. Say hello 👋</div>}
          {messages?.map((m, i) => (
            <div key={m.id}>
              {(i === 0 || messages[i - 1].createdAt.slice(0, 10) !== m.createdAt.slice(0, 10)) && (
                <div className="my-3 flex items-center gap-2 text-[11px] text-subtle">
                  <div className="h-px flex-1 bg-border" />
                  {fmtDate(m.createdAt, 'EEE d MMM')}
                  <div className="h-px flex-1 bg-border" />
                </div>
              )}
              <MessageRow m={m} channel={ch} onThread={() => setThread(m.id)} />
            </div>
          ))}
          <div ref={bottom} />
        </div>
        <div className="h-5 px-4 text-xs text-subtle">{typers.length ? `${typers.join(', ')} ${typers.length > 1 ? 'are' : 'is'} typing…` : ''}</div>
        {ch.canPost ? <Composer channelId={channelId} room={room} placeholder={`Message ${ch.type === 'dm' || ch.type === 'group' ? ch.name : `#${ch.name}`}`} /> : <div className="border-t border-border px-4 py-3 text-sm text-subtle">{ch.type === 'announcements' ? 'Only project leads and admins post announcements.' : 'You cannot post here.'}</div>}
      </div>
      {thread && <ThreadPanel messageId={thread} channel={ch} onClose={() => setThread(null)} />}
    </div>
  );
}

function Composer({ channelId, room, placeholder, threadParentId }: { channelId: string; room: string; placeholder: string; threadParentId?: string }) {
  const qc = useQueryClient();
  const [doc, setDoc] = useState<JSONContent | null>(null);
  const [key, setKey] = useState(0);
  const lastTyping = useRef(0);
  const send = useMutation({
    mutationFn: (body: JSONContent) => http.post<{ command?: true; ok?: boolean; reply?: string }>(`/channels/${channelId}/messages`, { body, threadParentId }),
    onSuccess: (r) => {
      // `/kanbix …` commands (FR-TIME-12) answer only the sender.
      if (r?.command) {
        if (r.ok) toast.success(r.reply);
        else toast.error(r.reply);
        if (r.ok) void qc.invalidateQueries({ queryKey: ['worklogs'] });
      }
      setDoc(null);
      setKey((k) => k + 1);
      void qc.invalidateQueries({ queryKey: ['messages', channelId] });
      if (threadParentId) void qc.invalidateQueries({ queryKey: ['thread', threadParentId] });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const submit = () => {
    if (doc && !isEmptyDoc(doc) && !send.isPending) send.mutate(doc);
  };
  return (
    <div className="border-t border-border px-4 py-2">
      <div className="rounded-md border border-border bg-surface px-2 py-1 focus-within:border-brand">
        <RichEditor
          key={key}
          value={null}
          minimal
          enterSubmits
          placeholder={`${placeholder} — Enter to send · /kanbix help for commands`}
          onChange={(d) => {
            setDoc(d);
            if (Date.now() - lastTyping.current > 2500) {
              lastTyping.current = Date.now();
              emitTyping(room);
            }
          }}
          onSubmit={submit}
        />
      </div>
    </div>
  );
}

function MessageRow({ m, channel, onThread, inThread }: { m: Msg; channel: ChannelDetail; onThread?: () => void; inThread?: boolean }) {
  const qc = useQueryClient();
  const me = useAuth((s) => s.user);
  const users = useUserMap();
  const [toItem, setToItem] = useState(false);
  const [moving, setMoving] = useState(false);
  const react = useMutation({ mutationFn: (emoji: string) => http.post(`/messages/${m.id}/reactions`, { emoji }), onSuccess: () => qc.invalidateQueries({ queryKey: ['messages', channel.id] }) });
  const del = useMutation({ mutationFn: () => http.del(`/messages/${m.id}`), onSuccess: () => qc.invalidateQueries({ queryKey: ['messages', channel.id] }) });
  const system = !m.authorId;
  const meta = m.meta as { type?: string; installUrls?: { production: string; sandbox: string }; label?: string } | null;
  return (
    <div className={cn('group relative flex gap-2 rounded px-1 py-1.5 hover:bg-muted/40', system && 'text-sm')}>
      <div className="w-7 shrink-0 pt-0.5">{system ? <div className="flex h-6 w-6 items-center justify-center rounded bg-brand-soft text-brand">K</div> : <UserChip userId={m.authorId} size={26} showName={false} />}</div>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2 text-xs">
          <span className="font-semibold">{system ? 'Kanbix' : (users.get(m.authorId!)?.displayName ?? 'Someone')}</span>
          <span className="text-subtle">{fmtDate(m.createdAt, 'HH:mm')}</span>
          {m.editedAt && !m.deleted && <span className="text-subtle">(edited)</span>}
          {m.kind === 'standup' && <Badge>stand-up</Badge>}
          {m.kind === 'announcement' && <Badge className="bg-warning/15 text-warning">announcement</Badge>}
        </div>
        {m.deleted ? (
          <div className="text-sm italic text-subtle">Message deleted</div>
        ) : meta?.type === 'build' && meta.installUrls ? (
          <div className="mt-1 rounded-md border border-border p-2">
            <div className="font-medium">{m.text.split('\n')[0]}</div>
            <div className="mt-1 flex gap-3 text-xs">
              <a href={meta.installUrls.production} target="_blank" rel="noreferrer" className="text-brand hover:underline">
                Install (production)
              </a>
              <a href={meta.installUrls.sandbox} target="_blank" rel="noreferrer" className="text-brand hover:underline">
                Install (sandbox)
              </a>
            </div>
          </div>
        ) : (
          <RichViewer value={m.body} className="prose-sm text-sm" />
        )}
        {m.linkedItem && (
          <div className="mt-1 text-xs">
            Work item: <ItemKeyLink itemKey={m.linkedItem.key} /> {m.linkedItem.title}
          </div>
        )}
        <div className="mt-1 flex flex-wrap items-center gap-1">
          {m.reactions.map((r) => (
            <button key={r.emoji} onClick={() => react.mutate(r.emoji)} className={cn('rounded-full border px-1.5 text-xs', r.mine ? 'border-brand bg-brand-soft' : 'border-border')}>
              {r.emoji} {r.count}
            </button>
          ))}
          {!inThread && m.replyCount > 0 && (
            <button onClick={onThread} className="text-xs font-medium text-brand hover:underline">
              {m.replyCount} {m.replyCount === 1 ? 'reply' : 'replies'}
            </button>
          )}
        </div>
      </div>
      {!m.deleted && (
        <div className="absolute right-1 top-1 hidden gap-0.5 rounded border border-border bg-surface p-0.5 shadow-sm group-hover:flex">
          <Menu>
            <MenuTrigger asChild>
              <button className="rounded p-1 hover:bg-muted" aria-label="React">
                <Smile className="h-3.5 w-3.5" />
              </button>
            </MenuTrigger>
            <MenuContent>
              <div className="flex gap-1 p-1">
                {EMOJI.map((e) => (
                  <button key={e} className="rounded p-1 hover:bg-muted" onClick={() => react.mutate(e)}>
                    {e}
                  </button>
                ))}
              </div>
            </MenuContent>
          </Menu>
          {!inThread && onThread && (
            <button className="rounded p-1 hover:bg-muted" onClick={onThread} aria-label="Reply in thread">
              <MessageSquare className="h-3.5 w-3.5" />
            </button>
          )}
          <Menu>
            <MenuTrigger asChild>
              <button className="rounded p-1 hover:bg-muted" aria-label="More actions">
                <MoreHorizontal className="h-3.5 w-3.5" />
              </button>
            </MenuTrigger>
            <MenuContent>
              {!m.linkedItem && <MenuItem onSelect={() => setToItem(true)}>Create work item…</MenuItem>}
              {!inThread && <MenuItem onSelect={() => setMoving(true)}>Move conversation to an item…</MenuItem>}
              {(m.authorId === me?.id || channel.canManage) && (
                <MenuItem danger onSelect={() => confirm('Delete this message?') && del.mutate()}>
                  Delete
                </MenuItem>
              )}
            </MenuContent>
          </Menu>
        </div>
      )}
      {toItem && <ToItemDialog m={m} channel={channel} onClose={() => setToItem(false)} />}
      {moving && <MoveDialog m={m} onClose={() => setMoving(false)} />}
    </div>
  );
}

function ThreadPanel({ messageId, channel, onClose }: { messageId: string; channel: ChannelDetail; onClose: () => void }) {
  const { data } = useQuery({ queryKey: ['thread', messageId], queryFn: () => http.get<{ parent: Msg; replies: Msg[] }>(`/messages/${messageId}/thread`) });
  return (
    <aside className="flex w-96 shrink-0 flex-col border-l border-border bg-surface">
      <div className="flex items-center border-b border-border px-3 py-2">
        <h2 className="text-sm font-semibold">Thread</h2>
        <button className="ml-auto rounded p-1 hover:bg-muted" onClick={onClose} aria-label="Close thread">
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-2 py-2">
        {data && <MessageRow m={data.parent} channel={channel} inThread />}
        <div className="my-2 h-px bg-border" />
        {data?.replies.map((r) => (
          <MessageRow key={r.id} m={r} channel={channel} inThread />
        ))}
      </div>
      {channel.canPost && <Composer channelId={channel.id} room={Rooms.channel(channel.id)} placeholder="Reply" threadParentId={messageId} />}
    </aside>
  );
}

function ToItemDialog({ m, channel, onClose }: { m: Msg; channel: ChannelDetail; onClose: () => void }) {
  const qc = useQueryClient();
  const { data: projects } = useProjects();
  const [projectKey, setProjectKey] = useState(channel.projectKey ?? projects?.[0]?.key ?? '');
  const [type, setType] = useState('task');
  const [title, setTitle] = useState(m.text.split('\n')[0].slice(0, 200));
  const run = useMutation({
    mutationFn: () => http.post<{ key: string }>(`/messages/${m.id}/to-work-item`, { projectKey: projectKey || projects?.[0]?.key, type, title }),
    onSuccess: (r) => {
      toast.success(`Created ${r.key}`);
      void qc.invalidateQueries({ queryKey: ['messages', channel.id] });
      onClose();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  return (
    <Dialog open onOpenChange={onClose} title="Create a work item from this message" footer={<Button onClick={() => run.mutate()} loading={run.isPending}>Create</Button>}>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Project">
            <Select value={projectKey} onChange={(e) => setProjectKey(e.target.value)}>
              {projects?.map((p) => (
                <option key={p.id} value={p.key}>
                  {p.key}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Type">
            <Select value={type} onChange={(e) => setType(e.target.value)}>
              <option value="task">Task</option>
              <option value="bug">Bug</option>
              <option value="story">Story</option>
              <option value="quick_task">Quick task</option>
            </Select>
          </Field>
        </div>
        <Field label="Title">
          <Input value={title} onChange={(e) => setTitle(e.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}

function MoveDialog({ m, onClose }: { m: Msg; onClose: () => void }) {
  const [itemKey, setItemKey] = useState('');
  const run = useMutation({
    mutationFn: () => http.post<{ key: string }>(`/messages/${m.id}/move-to-item`, { itemKey: itemKey.trim().toUpperCase() }),
    onSuccess: (r) => {
      toast.success(`Conversation added to ${r.key}`);
      onClose();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  return (
    <Dialog open onOpenChange={onClose} title="Move this conversation onto a work item" description="The message and its replies are added to the item as one quoted comment." footer={<Button onClick={() => run.mutate()} disabled={!itemKey.trim()} loading={run.isPending}>Move</Button>}>
      <Field label="Item key">
        <Input value={itemKey} onChange={(e) => setItemKey(e.target.value)} placeholder="KBX-42" autoFocus />
      </Field>
    </Dialog>
  );
}

function NewChannelDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data: projects } = useProjects();
  const { data: users } = useUsers();
  const [projectKey, setProjectKey] = useState(projects?.[0]?.key ?? '');
  const [name, setName] = useState('');
  const [isPrivate, setPrivate] = useState(false);
  const [members, setMembers] = useState<string[]>([]);
  const run = useMutation({
    mutationFn: () => http.post<{ id: string }>('/channels', { projectKey: projectKey || projects?.[0]?.key, name, isPrivate, memberIds: members }),
    onSuccess: (c) => {
      void qc.invalidateQueries({ queryKey: ['channels'] });
      navigate(`/chat/${c.id}`);
      onClose();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  return (
    <Dialog open onOpenChange={onClose} title="New channel" footer={<Button onClick={() => run.mutate()} disabled={!name.trim()} loading={run.isPending}>Create</Button>}>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Project">
            <Select value={projectKey} onChange={(e) => setProjectKey(e.target.value)}>
              {projects?.map((p) => (
                <option key={p.id} value={p.key}>
                  {p.key}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Name">
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="payments-squad" autoFocus />
          </Field>
        </div>
        <Switch checked={isPrivate} onChange={setPrivate} label="Private (members only)" />
        {isPrivate && (
          <Field label="Members">
            <div className="flex max-h-40 flex-wrap gap-1.5 overflow-auto">
              {users?.map((u) => (
                <label key={u.id} className="flex items-center gap-1 rounded border border-border px-1.5 py-0.5 text-xs">
                  <input type="checkbox" checked={members.includes(u.id)} onChange={(e) => setMembers(e.target.checked ? [...members, u.id] : members.filter((x) => x !== u.id))} />
                  {u.displayName}
                </label>
              ))}
            </div>
          </Field>
        )}
      </div>
    </Dialog>
  );
}

function NewDmDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const me = useAuth((s) => s.user);
  const { data: users } = useUsers();
  const [q, setQ] = useState('');
  const [picked, setPicked] = useState<string[]>([]);
  const run = useMutation({
    mutationFn: () => http.post<{ id: string }>('/dms', { userIds: picked }),
    onSuccess: (c) => {
      void qc.invalidateQueries({ queryKey: ['channels'] });
      navigate(`/chat/${c.id}`);
      onClose();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const list = (users ?? []).filter((u) => u.id !== me?.id && u.displayName.toLowerCase().includes(q.toLowerCase()));
  return (
    <Dialog open onOpenChange={onClose} title="New message" footer={<Button onClick={() => run.mutate()} disabled={!picked.length} loading={run.isPending}>Start conversation</Button>}>
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search people" autoFocus className="mb-2" />
      <div className="max-h-64 space-y-0.5 overflow-auto">
        {list.map((u) => (
          <label key={u.id} className="flex items-center gap-2 rounded px-2 py-1 text-sm hover:bg-muted">
            <input type="checkbox" checked={picked.includes(u.id)} onChange={(e) => setPicked(e.target.checked ? [...picked, u.id] : picked.filter((x) => x !== u.id))} />
            <Avatar name={u.displayName} url={u.avatarUrl} size={20} /> {u.displayName}
          </label>
        ))}
      </div>
    </Dialog>
  );
}

function ChannelCatchUp({ channelId }: { channelId: string }) {
  const { data: ai } = useAiStatus();
  if (!ai?.enabled || ai.features.catch_up === false) return null;
  return <CatchUpButton scope={{ kind: 'channel', id: channelId }} size="icon" variant="ghost" />;
}
